import { useState } from 'react';
import { AlertTriangle, Bell, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  useAuth,
  useBackInStockSubscription,
  useSubscribeBackInStock,
} from '@flote/app-core';
import { Button } from '@flote/ui';
import { useModal, ModalType } from '../../lib/modal';

interface Props {
  productId: number;
  productName: string;
}

/**
 * Back-in-stock subscribe form. Auth-gated. The "you're on the alert list"
 * state is derived from a list-mine query that returns subscriptions keyed by
 * BOTH the native UUID and the legacy B2BWave numeric ID — so we can match on
 * the storefront's b2bwaveProductId without a translation roundtrip.
 *
 * Subscribe is idempotent server-side: repeat clicks return 200 with `created: false`
 * and the toast distinguishes new vs existing.
 */
export function BackInStockSubscribeForm({ productId, productName }: Props) {
  const { isAuthenticated, customer } = useAuth();
  const { openModal } = useModal();
  const subscribeMutation = useSubscribeBackInStock();
  const { subscription, isLoading: isCheckingSubscription } = useBackInStockSubscription({
    b2bwaveProductId: isAuthenticated ? productId : null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isAuthenticated) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-4 space-y-3">
        <h4 className="flex items-center gap-1.5 font-medium text-amber-800">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600" />
          This item is currently out of stock
        </h4>
        <p className="text-sm text-amber-700">
          Sign in to get notified when {productName} is back in stock.
        </p>
        <Button
          type="button"
          variant="outline"
          className="w-full gap-2"
          onClick={() => openModal(ModalType.AUTH_MODAL, {})}
        >
          <Bell className="h-4 w-4" />
          Sign in to get notified
        </Button>
      </div>
    );
  }

  // While the list query is in flight on first render, render the skeleton-y
  // amber card without a CTA so we don't briefly show "Notify me" then flip
  // to "subscribed" when data lands.
  if (isCheckingSubscription) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-4 space-y-3">
        <h4 className="flex items-center gap-1.5 font-medium text-amber-800">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600" />
          This item is currently out of stock
        </h4>
        <div className="flex items-center gap-2 text-sm text-amber-700">
          <Loader2 className="h-4 w-4 animate-spin" />
          Checking your alerts…
        </div>
      </div>
    );
  }

  if (subscription) {
    return (
      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 space-y-2">
        <h4 className="flex items-center gap-1.5 font-medium text-emerald-800">
          <Bell className="h-5 w-5 flex-shrink-0 text-emerald-600" />
          You're on the alert list
        </h4>
        <p className="text-sm text-emerald-700">
          We'll email you at <span className="font-medium">{customer?.email}</span> when{' '}
          {productName} is back in stock.
        </p>
      </div>
    );
  }

  const handleSubscribe = async () => {
    setIsSubmitting(true);
    try {
      const result = await subscribeMutation.mutateAsync({
        body: {
          b2bwaveProductId: productId,
          sourceHostDomain: window.location.hostname,
        },
      });
      // ts-rest mutations only return success status codes here (201 fresh, 200 idempotent);
      // 404 / 422 / etc. throw and are surfaced by the global error handler
      // (apps/storefront/src/lib/hooks/useGlobalErrorHandler.ts) as a toast
      // using the response body's `message` field. We deliberately don't toast
      // again here — that would double up.
      if (result.status === 201) {
        toast.success(`We'll let you know when ${productName} is back in stock.`);
      } else {
        toast.success("You're already on the list — we'll email you when it's back.");
      }
      // Hook's onSuccess invalidates the list query → next render the
      // `subscription` derived above flips to non-null, swapping us into the
      // "You're on the alert list" branch automatically. No local state needed.
    } catch (err: unknown) {
      // Don't toast here — the global error handler (mutation cache subscriber)
      // already shows a toast with the server-provided message. Just log so the
      // error surface in DevTools is searchable.
      console.error('back-in-stock subscribe error', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-4 space-y-3">
      <h4 className="flex items-center gap-1.5 font-medium text-amber-800">
        <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600" />
        This item is currently out of stock
      </h4>
      <p className="text-sm text-amber-700">
        We'll email you at <span className="font-medium">{customer?.email}</span> when it's back.
      </p>
      <Button
        type="button"
        variant="outline"
        className="w-full gap-2"
        disabled={isSubmitting}
        onClick={handleSubscribe}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Subscribing…
          </>
        ) : (
          <>
            <Bell className="h-4 w-4" />
            Notify me when in stock
          </>
        )}
      </Button>
    </div>
  );
}
